<template>
  <div class="select-replacement__container">
    <change-status-header :displayName="fund.displayName" />
    <div class="select-replacement__content">
      <p class="select-replacement__title">
        Replace {{ fund.displayName }} in product {{ product.name }} ({{ product.productCode }})
      </p>
      <div class="select-replacement__actions-bar">
        <filterBar
          @updateFilters="updateFilters"
          :fiduciary="selectedFiduciary"
          :client="selectedClient"
          :hideFilters="hideFilters"
          :clearAll="clearAllFilters"
        />
        <search-field
          @onChange="changeSearchTerm"
          :clear="clearLocalSearchField"
          placeholder="Search"
        />
      </div>
      <loader-spinner v-if="loading" />
      <div v-else class="select-replacement__data">
        <table class="mds-data-table">
          <thead class="mds-data-table__header">
            <tr class="mds-data-table__row">
              <th class="mds-data-table__header-cell" width="*" scope="col">
                <div class="mds-data-table__header-cell-inner">
                  Fund Name
                </div>
              </th>
              <th class="mds-data-table__header-cell" width="120" scope="col">
                <div class="mds-data-table__header-cell-inner">
                  Ticker
                </div>
              </th>
              <th class="mds-data-table__header-cell" width="120" scope="col">
                <div class="mds-data-table__header-cell-inner">
                  SecID
                </div>
              </th>
              <th class="mds-data-table__header-cell" width="120" scope="col">
                <div class="mds-data-table__header-cell-inner">
                  Category
                </div>
              </th>
              <th class="mds-data-table__header-cell" width="84" scope="col">
                <div
                  class="mds-data-table__header-cell-inner mds-data-table__header-cell-inner--center"
                >
                  Status
                </div>
              </th>
              <th class="mds-data-table__header-cell" width="84" scope="col">
                <div
                  class="mds-data-table__header-cell-inner mds-data-table__header-cell-inner--center"
                >
                  Pending
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="mds-data-table__body">
            <tr
              v-for="(record, index) in records"
              :key="index"
              class="mds-data-table__row"
              :class="{ selected: selectedIndex === index }"
            >
              <td class="mds-data-table__cell">
                <label
                  class="mds-form__radio-button"
                  :for="`radio-${record.fiduciaryFund.id}-${index}`"
                >
                  <input
                    name="select-replacement"
                    type="radio"
                    class="mds-form__radio-button-input"
                    :id="`radio-${record.fiduciaryFund.id}-${index}`"
                    :value="index"
                    v-model="selectedIndex"
                  />
                  <span class="mds-form__radio-button-visible-wrap">
                    <span class="mds-form__radio-button-visual"></span>
                    <span class="mds-form__radio-button-text">
                      {{ record.fiduciaryFund.displayName }}
                    </span>
                  </span>
                </label>
              </td>
              <td class="mds-data-table__cell">
                {{ record.fiduciaryFund.ticker }}
              </td>
              <td class="mds-data-table__cell">
                {{ record.fiduciaryFund.secID }}
              </td>
              <td class="mds-data-table__cell">
                {{ record.fiduciaryFund.category }}
              </td>
              <td class="mds-data-table__cell">
                <status-icons :status="[{ name: record.fiduciaryFund.fundStatus.name }]" />
              </td>
              <td class="mds-data-table__cell">
                <div v-if="record.fiduciaryFund.isPending" class="pending-icon mds-icon">
                  <svg class="mds-icon" aria-hidden="true">
                    <use
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                      xlink:href="static/mds/icons/mds.svg#clock-back"
                    ></use>
                  </svg>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="select-replacement__actions">
      <button
        class="mds-button mds-button--primary"
        type="button"
        @click="selectReplacement"
        :disabled="processing || disableSubmit"
      >
        Select Replacement
      </button>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'vuex';
import {
  merge,
  sortBy,
} from 'lodash';

import LoaderSpinner from '@/components/atoms/LoadingSpinner';
import ChangeStatusHeader from '@/components/atoms/ChangeStatusHeader';
import StatusIcons from '@/components/atoms/StatusIcons';
import SearchField from '@/components/atoms/SearchField';
import FilterBar from '@/components/organisms/FilterBar';
import {
  getFiduciaryClientDefaultFunds,
  getFiduciaryClientFilterReplacementsByCategory,
  replacePendingProductFiduciaryFunds,
} from '@/services/SelectReplacement';

export default {
  name: 'select-replacement',
  components: {
    LoaderSpinner,
    ChangeStatusHeader,
    StatusIcons,
    SearchField,
    FilterBar,
  },
  props: {
    data: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      loading: true,
      processing: false,
      product: {},
      fund: {},
      records: [],
      defaultFunds: [],
      categorizedFunds: [],
      selectedIndex: -1,
      selected: undefined,
      hideFilters: {
        classes: true,
        statuses: true,
        products: true,
        updates: true,
        dateRange: true,
      },
      categories: [],
      allStatusMode: false,
      clearAllFilters: false,
      clearLocalSearchField: false,
      commonParams: {},
    };
  },
  computed: {
    ...mapState({
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.grid.client.selected,
    }),
    disableSubmit() {
      return this.selectedIndex === -1;
    },
  },
  watch: {
    selectedIndex(newValue) {
      const index = parseInt(newValue, 10);
      if (index >= 0) {
        this.selected = this.records[index];
      } else {
        this.selected = undefined;
      }
    },
    categories() {
      this.filterReplacements();
      this.clearAllFilters = false;
    },
  },
  created() {
    const { fund = {}, product = {} } = this.data;
    const { id: removedFiduciaryFund } = fund;
    const { id: productId } = product;

    this.fund = fund;
    this.product = product;
    this.loading = true;
    this.commonParams = {
      selectedClient: this.selectedClient,
      removedFiduciaryFund,
      productId,
    };
    getFiduciaryClientDefaultFunds({
      selectedClient: this.selectedClient,
      removedFiduciaryFund,
      productId,
    }).then((result) => {
      if (result.status.success) {
        const { data = [] } = result;
        const formattedData = this._formatPayload(data);
        this.records = formattedData;
        this.defaultFunds = formattedData;
        this.clearAllFilters = true;
      }
    }).catch((error) => {
      const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
      if (httpStatus === 401) {
        this.$router.push('/Login');
      }
    }).finally(() => {
      this.loading = false;
    });
  },
  methods: {
    ...mapActions({
      closeFullscreenModal: 'closeFullscreenModal',
    }),
    selectReplacement() {
      this.processing = true;
      const { id: fiduciaryFundId } = this.fund;
      const { id: productId } = this.product;
      const { id: replacementFiduciaryFundId } = this.selected.fiduciaryFund;

      const payload = {
        fiduciaryFundId,
        productId,
        replacementFiduciaryFundId,
      };

      replacePendingProductFiduciaryFunds(payload)
        .then((result) => {
          this.closeFullscreenModal();
          this.data.callback(this.selected);
          return result;
        })
        .catch((error) => {
          const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
          if (httpStatus.status === 401) {
            this.$router.push('/Login');
          }
        })
        .finally(() => {
          this.processing = false;
        });
    },
    updateFilters(filters, allStatus) {
      this.categories = filters.categories;
      this.allStatusMode = allStatus;
    },
    filterReplacements() {
      if (this.categories.length > 0) {
        // Clear search
        this.clearAllFilters = false;
        this.clearLocalSearchField = true;

        const categoriesData = this.categories
          .map((category) => encodeURIComponent(category))
          .join(',');
        this.loading = true;
        const { selectedClient, removedFiduciaryFund, productId } = this.commonParams;

        getFiduciaryClientFilterReplacementsByCategory({
          selectedClient,
          removedFiduciaryFund,
          productId,
          categories: categoriesData,
        }).then((result) => {
          const { data = [] } = result;
          const formattedCategoryData = this._formatPayload(data);
          this.records = formattedCategoryData;
          this.categorizedFunds = formattedCategoryData;
        }).catch((error) => {
          const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
          if (httpStatus === 401) {
            this.$router.push('/Login');
          }
        }).finally(() => {
          this.loading = false;
          this.selectedIndex = -1;
        });
      } else {
        // If the filters are cleared
        this.records = this.defaultFunds;
        this.selectedIndex = -1;
        this.clearAllFilters = true;
      }
    },
    changeSearchTerm(value) {
      const fundsToSearch = this.categories.length ? this.categorizedFunds : this.defaultFunds;
      if (value) {
        const keyword = String(value).toLowerCase();
        // inside fiduciaryFund object
        this.records = this.multipleKeysArrayFilter(fundsToSearch, keyword);
      } else {
        // If the search is cleared
        this.records = fundsToSearch;
      }
    },
    multipleKeysArrayFilter(targetArray, keyword) {
      if (!targetArray || targetArray.length === 0) { return []; }
      return targetArray.filter((obj) => {
        if (obj?.fiduciaryFund) {
          const fiduFundsObj = obj?.fiduciaryFund;
          /* eslint-disable-next-line max-len */
          return this.containsValueByKey(fiduFundsObj, 'displayName', keyword) || this.containsValueByKey(fiduFundsObj, 'secID', keyword) || this.containsValueByKey(fiduFundsObj, 'cusip', keyword) || this.containsValueByKey(fiduFundsObj, 'category', keyword) || this.containsValueByKey(fiduFundsObj, 'ticker', keyword);
        }
        return true;
      });
    },
    containsValueByKey(obj, key, value) {
      return obj[key] && String(obj[key]).toLowerCase().includes(String(value).toLowerCase());
    },
    _formatPayload(items) {
      /* eslint-disable no-param-reassign */
      return sortBy(items, 'fiduciaryFund.displayName')
        .map((item) => {
          const { fiduciaryFund = {} } = item;

          // Extract first child from Array property
          const {
            fundStatus: [{ fundStatus }],
            pendingStatus,
            psaCategory: [
              {
                category: { name: categoryName },
              },
            ],
          } = fiduciaryFund;

          // Override Pending Status if the fund have one
          item.fiduciaryFund.fundStatus = fundStatus;
          if (pendingStatus && pendingStatus.length > 0) {
            const [{ fundStatus: newFundStatus }] = pendingStatus;
            item.fiduciaryFund.fundStatus = merge({}, fundStatus, newFundStatus);
          }

          item.fiduciaryFund.category = categoryName;

          return item;
        })
        .filter((item) => item.fiduciaryFund.fundStatus.name !== 'Removed');
      /* eslint-enable no-param-reassign */
    },
  },
};
</script>
<style lang="scss">
.select-replacement {
  &__container {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
  }
  .change-status__header {
    flex: initial;
  }
  &__content {
    $box-height: calc(100% - 152px - 32px); // Substract: header height - actions height
    display: flex;
    flex-direction: column;
    flex: auto;
    min-height: $box-height;
    max-height: $box-height;
    padding: $mds-space-4-x $mds-space-8-x;
  }
  &__title {
    flex: initial;
    font-family: $mds-font-family-sans-serif;
    font-weight: $mds-font-weight-light;
    margin: 0 0 $mds-space-1-x;
  }
  &__actions-bar {
    flex: initial;
    margin-bottom: $mds-space-4-x;
    position: relative;

    // TODO Make independent Position and Appearance of Search Field
    @mixin placeholder {
      &::-webkit-input-placeholder {
        @content;
      }
      &:-moz-input-placeholder {
        @content;
      }
      &::-moz-input-placeholder {
        @content;
      }
      &:-ms-input-placeholder {
        @content;
      }
    }
    .filter-bar__container {
      padding-left: 0;
      padding-top: $mds-space-2-x;
    }
    & .search-field {
      &__container {
        position: absolute;
        right: 0;
        bottom: 0;
        height: $mds-space-4-x;
        width: 200px;
      }
      &__input.mds-search-field__input {
        color: $mds-color-neutral-37;
        border-color: $mds-color-neutral-37;
        @include placeholder {
          color: $mds-color-neutral-37;
        }
      }
      .mds-search-field__input:focus:enabled + svg {
        fill: $mds-color-neutral-20;
      }
      .mds-search-field__input:hover:enabled + svg {
        fill: $mds-color-neutral-20;
      }
      &__search-icon.mds-icon,
      &__clear-icon.mds-icon.mds-button__icon {
        fill: $mds-color-neutral-37;
      }
    }
  }
  &__data {
    flex: auto;
    margin-top: $mds-space-inset-2-x;
    overflow-x: hidden;
    overflow-y: auto;

    .mds-data-table__header-cell-inner {
      @include mds-body-text-s();
      color: $mds-color-black;
      font-weight: $mds-font-weight-bold;
    }
    .mds-data-table__row.selected {
      background-color: #f7fffd;
    }
    .mds-data-table__cell {
      padding-bottom: $mds-space-inset-2-x;
      padding-top: $mds-space-inset-2-x;
    }
    .status-icons,
    .pending-icon {
      margin-left: $mds-space-inset-1-x;
    }
    .mds-form__radio-button {
      padding-left: $mds-space-inset-1-x;
    }
  }
  &__actions {
    flex: initial;
    padding: 0 $mds-space-inset-8-x $mds-space-inset-4-x $mds-space-inset-8-x;
    text-align: right;
  }
}
</style>
